import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertCircle,
  ArrowDownLeft,
  ArrowUpRight,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Coins,
  Key,
  RefreshCw,
  Wallet,
  X,
} from 'lucide-react';
import { walletService } from '../../services/walletService';
import type { Transaction } from '../../types/wallet';
import PortalPageHeader from '../../components/student-portal/PortalPageHeader';
import {
  formatCoins,
  formatCoinsDelta,
  formatTransactionDescription,
  formatWalletDate,
  getTransactionTypeLabel,
  isValidWalletCode,
} from '../../utils/walletLabels';

const PortalWallet: React.FC = () => {
  const [balance, setBalance] = useState<number | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [walletCode, setWalletCode] = useState('');
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [redeemError, setRedeemError] = useState<string | null>(null);
  const [showRedeemSuccess, setShowRedeemSuccess] = useState(false);
  const [addedCoins, setAddedCoins] = useState(0);
  const [newBalanceAfterRedeem, setNewBalanceAfterRedeem] = useState(0);

  const loadWallet = useCallback(async (page = 1) => {
    setIsLoading(true);
    setLoadError(null);
    const result = await walletService.getMyHistory(page, 10);
    if (result.success) {
      setBalance(result.data.history.currentBalance);
      setTransactions(result.data.history.transactions);
      setPageNumber(result.data.pageNumber);
      setTotalPages(result.data.totalPages);
    } else {
      setLoadError(result.error);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    loadWallet(1);
  }, [loadWallet]);

  useEffect(() => {
    if (!showRedeemSuccess) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowRedeemSuccess(false);
    };
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [showRedeemSuccess]);

  const handleRedeem = async (e: React.FormEvent) => {
    e.preventDefault();
    setRedeemError(null);

    const code = walletCode.trim();
    if (!isValidWalletCode(code)) {
      setRedeemError('كود المحفظة يجب أن يكون 7 أرقام');
      return;
    }

    setIsRedeeming(true);
    const result = await walletService.redeemWalletCode(code);
    setIsRedeeming(false);

    if (result.success) {
      setBalance(result.data.newBalance);
      setAddedCoins(result.data.addedCoins);
      setNewBalanceAfterRedeem(result.data.newBalance);
      setWalletCode('');
      setShowRedeemSuccess(true);
      loadWallet(1);
    } else {
      setRedeemError(result.error);
    }
  };

  return (
    <div className="relative overflow-x-hidden px-4 pb-8 pt-4 sm:px-6 sm:pt-6 lg:px-8 lg:pt-8" dir="rtl">
      {showRedeemSuccess && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
          dir="rtl"
          onClick={() => setShowRedeemSuccess(false)}
          role="presentation"
        >
          <div
            className="relative mx-4 w-full max-w-sm rounded-2xl border-2 border-teal-100 bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="wallet-redeem-success-title"
          >
            <button
              type="button"
              onClick={() => setShowRedeemSuccess(false)}
              className="absolute left-3 top-3 flex min-h-11 min-w-11 items-center justify-center rounded-xl text-gray-400 hover:bg-teal-50 hover:text-slate-900"
              aria-label="إغلاق"
            >
              <X size={18} />
            </button>
            <div className="pt-2 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-teal-50 text-teal-600">
                <CheckCircle className="h-8 w-8" />
              </div>
              <h3
                id="wallet-redeem-success-title"
                className="mb-2 text-xl font-extrabold text-slate-900"
              >
                تمام كده!
              </h3>
              <p className="mb-1 text-sm text-slate-600">الكود اتشحن بنجاح</p>
              <p className="text-sm text-slate-500">
                اتضاف{' '}
                <span className="font-semibold text-slate-900">
                  {formatCoins(addedCoins)} عملة
                </span>{' '}
                لمحفظتك
              </p>
              <p className="mt-3 rounded-xl border border-teal-100 bg-teal-50/70 px-3 py-2.5 text-sm text-slate-900">
                رصيدك دلوقتي:{' '}
                <span className="font-bold">{formatCoins(newBalanceAfterRedeem)} عملة</span>
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowRedeemSuccess(false)}
              className="mt-6 flex min-h-12 w-full items-center justify-center rounded-xl bg-teal-600 px-4 text-sm font-semibold text-white hover:bg-teal-700"
            >
              تمام
            </button>
          </div>
        </div>
      )}

      <div className="mb-5 rounded-2xl border-2 border-teal-100 bg-white px-4 py-5 sm:mb-8 sm:px-6 sm:py-6">
        <PortalPageHeader
          eyebrow="محفظتي"
          title="رصيدك ومعاملاتك"
          description="شحن المحفظة بكود واستخدام الرصيد في شراء الكورسات"
          icon={<Wallet className="h-4 w-4" />}
        />
      </div>

      <div className="mb-5 rounded-2xl border-2 border-teal-600 bg-teal-600 p-4 text-white shadow-lg shadow-teal-700/20 sm:mb-8 sm:p-6 lg:p-8">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="mb-1 text-xs text-teal-200 sm:text-sm">الرصيد الحالي</p>
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <Coins className="h-6 w-6 shrink-0 text-teal-200 sm:h-7 sm:w-7" />
              {isLoading ? (
                <div className="h-9 w-24 animate-pulse rounded-lg bg-white/20 sm:h-10" />
              ) : (
                <span className="break-all text-3xl font-extrabold sm:text-4xl lg:text-5xl">
                  {formatCoins(balance ?? 0)}
                </span>
              )}
              <span className="text-base text-teal-200 sm:text-lg">عملة</span>
            </div>
            <p className="mt-2 text-xs text-teal-200/80">1 عملة = 1 جنيه مصري</p>
          </div>
          <button
            type="button"
            onClick={() => loadWallet(pageNumber)}
            disabled={isLoading}
            className="min-h-11 min-w-11 shrink-0 rounded-xl bg-white/15 p-2.5 transition-colors hover:bg-white/25 disabled:opacity-50"
            aria-label="تحديث الرصيد"
          >
            <RefreshCw className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {loadError && (
        <div className="mb-5 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 sm:p-4">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{loadError}</span>
        </div>
      )}

      <div className="mb-5 rounded-2xl border-2 border-teal-100 bg-white p-4 sm:mb-8 sm:p-6">
        <h2 className="mb-1 flex items-center gap-2 text-base font-extrabold text-slate-900 sm:text-lg">
          <Key className="h-5 w-5 shrink-0 text-teal-600" />
          شحن بكود المحفظة
        </h2>
        <p className="mb-4 text-xs text-slate-600 sm:text-sm">
          أدخل كود المحفظة المكوّن من 7 أرقام
        </p>

        <form onSubmit={handleRedeem} className="space-y-3">
          <input
            type="text"
            inputMode="numeric"
            maxLength={7}
            value={walletCode}
            onChange={(e) => {
              setWalletCode(e.target.value.replace(/\D/g, '').slice(0, 7));
              setRedeemError(null);
            }}
            placeholder="0000000"
            className="min-h-12 w-full rounded-xl border-2 border-teal-100 bg-white px-3 py-3 text-center font-mono text-lg tracking-[0.25em] focus:border-teal-500 focus:outline-none sm:px-4 sm:text-xl sm:tracking-[0.3em]"
            disabled={isRedeeming}
            autoComplete="off"
          />
          {redeemError && (
            <p className="flex items-start gap-1.5 text-sm text-red-600">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              {redeemError}
            </p>
          )}
          <button
            type="submit"
            disabled={isRedeeming || walletCode.length !== 7}
            className="min-h-11 w-full rounded-xl bg-teal-600 py-3 font-semibold text-white transition-all hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isRedeeming ? 'جاري التحقق...' : 'استخدام الكود'}
          </button>
        </form>
      </div>

      <div className="rounded-2xl border-2 border-teal-100 bg-white">
        <div className="border-b border-teal-100 px-4 py-3 sm:px-6 sm:py-4">
          <h2 className="text-base font-extrabold text-slate-900 sm:text-lg">سجل المعاملات</h2>
        </div>

        {isLoading ? (
          <div className="p-6 text-center text-sm text-slate-500 sm:p-8">جاري التحميل...</div>
        ) : transactions.length === 0 ? (
          <div className="p-8 text-center text-slate-500 sm:p-12">
            <Wallet className="mx-auto mb-3 h-10 w-10 text-slate-300 sm:h-12 sm:w-12" />
            <p className="text-sm sm:text-base">لا توجد معاملات بعد</p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-teal-50">
              {transactions.map((tx) => {
                const isCredit = tx.amount > 0;
                return (
                  <div key={tx.id} className="px-4 py-3 transition-colors hover:bg-teal-50/40 sm:px-6 sm:py-4">
                    <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:gap-4">
                      <div className="flex min-w-0 flex-1 items-start gap-3">
                        <div
                          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                            isCredit ? 'bg-teal-50 text-teal-600' : 'bg-red-100 text-red-600'
                          }`}
                        >
                          {isCredit ? <ArrowDownLeft className="h-5 w-5" /> : <ArrowUpRight className="h-5 w-5" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="line-clamp-2 text-sm font-semibold leading-snug text-slate-900">
                            {formatTransactionDescription(tx.description, tx.type)}
                          </p>
                          <p className="mt-1 text-xs leading-relaxed text-slate-500">
                            {getTransactionTypeLabel(tx.type)}
                          </p>
                          <p className="mt-0.5 text-xs text-slate-400 sm:hidden">
                            {formatWalletDate(tx.createdAt)}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-3 border-t border-teal-50 ps-[3.25rem] pt-2.5 sm:flex-col sm:items-end sm:gap-0.5 sm:border-0 sm:ps-0 sm:pt-0">
                        <p className={`text-base font-bold sm:text-sm ${isCredit ? 'text-teal-600' : 'text-red-600'}`}>
                          {formatCoinsDelta(tx.amount)} عملة
                        </p>
                        <p className="text-xs text-slate-400">الرصيد: {formatCoins(tx.balanceAfter)}</p>
                        <p className="mt-0.5 hidden text-xs text-slate-400 sm:block">
                          {formatWalletDate(tx.createdAt)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="flex flex-col gap-3 border-t border-teal-100 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-4">
                <span className="text-center text-sm text-slate-500 sm:order-2">
                  صفحة {pageNumber} من {totalPages}
                </span>
                <div className="grid grid-cols-2 gap-2 sm:contents">
                  <button
                    type="button"
                    onClick={() => loadWallet(pageNumber - 1)}
                    disabled={pageNumber <= 1 || isLoading}
                    className="flex min-h-11 items-center justify-center gap-1 rounded-xl border-2 border-teal-100 px-3 py-2 text-sm font-medium text-slate-900 hover:bg-teal-50 disabled:opacity-40 sm:order-1"
                  >
                    <ChevronRight className="h-4 w-4" />
                    السابق
                  </button>
                  <button
                    type="button"
                    onClick={() => loadWallet(pageNumber + 1)}
                    disabled={pageNumber >= totalPages || isLoading}
                    className="flex min-h-11 items-center justify-center gap-1 rounded-xl border-2 border-teal-100 px-3 py-2 text-sm font-medium text-slate-900 hover:bg-teal-50 disabled:opacity-40 sm:order-3"
                  >
                    التالي
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PortalWallet;
